import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { EnhancedModal, Body, Footer, Header } from 'common/components/EnhancedModal/EnhancedModal';
import { notifySuccess } from 'common/utils/notifier.utils';
import { ELimitsPeriod } from 'domains/limits/enums/limits.enum';
import { useSetLimitsOperationsMutation, useSetLimitsUsersMutation } from 'domains/limits/queries/limits.query';
import {
  SBottomButtonsWrapper,
  SFormAmountWrapper,
  SInfoField,
  SInfoFieldLabel,
  SInfoFieldValue,
} from './modal.styles';
import { ETypes } from '../enums/limits.enum';
import { TStandardLimitsModalData } from '../types/standard-limits.types';

enum EFormFields {
  Days = 'days',
  Min = 'min',
  Max = 'max',
}

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TStandardLimitsModalData;
  tableType: ETypes;
  invalidateDataTableHandler: () => Promise<void>;
};

const SetLimitsModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    invalidateDataTableHandler,
    tableType,
    data: {
      accountTypeGuid,
      counterpartyLabel,
      counterpartyGUID,
      segmentLabel,
      typeLabel,
      periodLabel,
      periodKey,
      maxAmount,
      minAmount,
      amountPeriodKey,
      assetCode,
      assetIssuer,
    },
  } = props;
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const { mutateAsync: mutateAsyncSetLimitsUsersMutation } = useSetLimitsUsersMutation();

  const { mutateAsync: mutateAsyncSetLimitsOperationsMutation } = useSetLimitsOperationsMutation();

  const positiveNumbersValidationRegExp = '^[+]?\\d+(\\.\\d+)?$';

  useEffect(() => {
    form.setFields([
      {
        name: EFormFields.Days,
        value: amountPeriodKey,
      },
      {
        name: EFormFields.Min,
        value: minAmount,
      },
      {
        name: EFormFields.Max,
        value: maxAmount,
      },
    ]);
  }, [minAmount, maxAmount]);

  const onFinishFormHandler = async (data: Record<EFormFields, string>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);

      const { min, max, days } = data;

      if (tableType === ETypes.User) {
        const limitsUsersReqData = {
          account_type_guid: accountTypeGuid,
          asset_code: assetCode,
          asset_issuer: assetIssuer,
          max: Number(max),
          period: periodKey as ELimitsPeriod,
          period_key: Number(days) || (amountPeriodKey as number),
        };
        await mutateAsyncSetLimitsUsersMutation({
          counterpartyGUID,
          requestData: limitsUsersReqData,
        });
      } else if (tableType === ETypes.Operation) {
        const limitsOperationsReqData = {
          account_type_guid: accountTypeGuid,
          asset_code: assetCode,
          asset_issuer: assetIssuer,
          max: Number(max),
          min: Number(min),
        };
        await mutateAsyncSetLimitsOperationsMutation({
          counterpartyGUID,
          requestData: limitsOperationsReqData,
        });
      }

      await invalidateDataTableHandler();

      notifySuccess('limits.notification.success');

      onCancelModalHandler();
      setIsSubmitBtnLoading(false);
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  return (
    <EnhancedModal open={isOpen} onCancel={onCancelModalHandler} width="384px">
      <Form form={form} onFinish={onFinishFormHandler}>
        <Header>{t('limits.standardLimits.modal.edit.title')}</Header>
        <Body>
          <SInfoField>
            <SInfoFieldLabel>{t('limits.standardLimits.modal.edit.fields.label.counterparty')}</SInfoFieldLabel>
            <SInfoFieldValue>{counterpartyLabel}</SInfoFieldValue>
          </SInfoField>
          <SInfoField>
            <SInfoFieldLabel>{t('limits.standardLimits.modal.edit.fields.label.period')}</SInfoFieldLabel>
            <SInfoFieldValue>{periodLabel}</SInfoFieldValue>
          </SInfoField>
          <SInfoField>
            <SInfoFieldLabel>{t('limits.standardLimits.modal.edit.fields.label.segment')}</SInfoFieldLabel>
            <SInfoFieldValue>{segmentLabel}</SInfoFieldValue>
          </SInfoField>
          <SInfoField>
            <SInfoFieldLabel>{t('limits.standardLimits.modal.edit.fields.label.type')}</SInfoFieldLabel>
            <SInfoFieldValue>{typeLabel}</SInfoFieldValue>
          </SInfoField>
          <SInfoField>
            <SInfoFieldLabel>{t('limits.standardLimits.modal.edit.fields.label.currency')}</SInfoFieldLabel>
            <SInfoFieldValue>{assetCode}</SInfoFieldValue>
          </SInfoField>
          {periodKey === ELimitsPeriod.Days && (
            <Form.Item name={EFormFields.Days} label={t('limits.standardLimits.modal.edit.fields.label.days')}>
              <Input suffix={t('limits.standardLimits.modal.edit.fields.suffix.days')} />
            </Form.Item>
          )}
          <SFormAmountWrapper>
            <Form.Item
              name={EFormFields.Min}
              label={t('limits.standardLimits.modal.edit.fields.label.min')}
              rules={[
                {
                  required: true,
                  message: t('limits.standardLimits.modal.edit.fields.validation.required') as string,
                },
                {
                  pattern: new RegExp(positiveNumbersValidationRegExp),
                  message: t('limits.standardLimits.modal.edit.fields.validation.required') as string,
                },
              ]}
            >
              <Input disabled={tableType !== ETypes.Operation} suffix={assetCode} />
            </Form.Item>
            <Form.Item
              name={EFormFields.Max}
              label={t('limits.standardLimits.modal.edit.fields.label.max')}
              rules={[
                {
                  required: true,
                  message: t('limits.standardLimits.modal.edit.fields.validation.required') as string,
                },
                {
                  pattern: new RegExp(positiveNumbersValidationRegExp),
                  message: t('limits.standardLimits.modal.edit.fields.validation.required') as string,
                },
                ({ getFieldValue }): { validator(_: any, value: any): Promise<any> } => ({
                  validator(_, value): Promise<any> {
                    if (Number(getFieldValue(EFormFields.Min)) < Number(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t('limits.standardLimits.modal.edit.fields.validation.maxGreaterMin') as string)
                    );
                  },
                }),
              ]}
            >
              <Input suffix={assetCode} />
            </Form.Item>
          </SFormAmountWrapper>
        </Body>
        <Footer>
          <SBottomButtonsWrapper>
            <Button onClick={onCancelModalHandler}>{t('limits.standardLimits.modal.edit.button.cancel')}</Button>
            <Button loading={isSubmitBtnLoading} htmlType="submit" type="primary">
              {t('limits.standardLimits.modal.edit.button.submit')}
            </Button>
          </SBottomButtonsWrapper>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default SetLimitsModal;
